'use client'

import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  filterDropdownTriggerClassName,
  filterSheetNestedSelectContentClassName,
  filterSheetSelectContentSideOffset,
  filterSheetSelectItemClassName,
  filterSheetSelectItemTextClassName,
} from '@/components/shared/filterSheetStyles'

const FilterSheetSelect = SelectPrimitive.Root

const FilterSheetSelectValue = SelectPrimitive.Value

const FilterSheetSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(filterDropdownTriggerClassName, className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
FilterSheetSelectTrigger.displayName = 'FilterSheetSelectTrigger'

const FilterSheetSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', sideOffset = filterSheetSelectContentSideOffset, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(filterSheetNestedSelectContentClassName, className)}
      position={position}
      sideOffset={sideOffset}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          'filter-sheet-select-viewport p-0',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
FilterSheetSelectContent.displayName = 'FilterSheetSelectContent'

const FilterSheetSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(filterSheetSelectItemClassName, className)}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText className={filterSheetSelectItemTextClassName}>
      {children}
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
FilterSheetSelectItem.displayName = 'FilterSheetSelectItem'

export {
  FilterSheetSelect,
  FilterSheetSelectValue,
  FilterSheetSelectTrigger,
  FilterSheetSelectContent,
  FilterSheetSelectItem,
}
